import type { EntityDefinitions } from '@/types/eds/Definitions';
import type { EDSLayoutSlots } from '@/types/eds/EDSLayoutSlots';
import type { EdsCategoriesRecord } from '@/types/eds/EdsCategoriesRecord';
import type { RawEntityPropertyValidationAllowedValue } from '@/types/eds/RawDefinitions';
import type {
  EDSV2ArrayResponse,
  EDSV2OverrideRenderDefinitionResponse,
  EDSV2PropertyValue,
  EDSV2PropertyValueLink,
  EDSV2Response,
} from '@/types/eds/api/responses/EDSV2Response';
import type { NormalizedV2Property, NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { EDSAdapterServiceNormalizeConfiguration } from '@/features/eds/services/types/EDSAdapterServiceInjectionConfig';
import { EdsDataTypes } from '@/features/eds/enums/EdsDataTypes';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsDynamicOverrideMode } from '@/features/eds/enums/EdsDynamicOverrideMode';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsRawDefinitionsService } from '@/features/eds/services/EdsRawDefinitionsService/EdsRawDefinitionsService';
import { EdsStorageService } from '@/features/eds/services/EdsStorageService/EdsStorageService';
import { edsCheckStoredEntityExpired } from '@/features/eds/services/utils/edsCheckStoredEntityExpired';
import { edsToCodeFormat, edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { mergeSlots } from '@/features/eds/utils/mergeSlots';
import FormatService from '@/services/FormatService/FormatService';
import MaskService from '@/services/MaskService/MaskService';
import MergeService from '@/services/MergeService/MergeService';
import TypeService from '@/services/TypeService/TypeService';
import { selectYesNoOptions } from '../constants/selectYesNoOptions';

class EdsAdapterService {
  public static async getEntityDefinition(
    entityCode: EdsEntityCodes,
    forceUpdate: boolean = false,
    response?: EDSV2Response,
    section?: string,
    overrideMode?: EdsDynamicOverrideMode,
    shouldUpdateAccordingToOverride: boolean = true,
  ): Promise<EntityDefinitions | null> {
    const { entity, timeStamp } = EdsStorageService.getEntity(entityCode, section);

    if (!entity || forceUpdate || edsCheckStoredEntityExpired(timeStamp)) {
      let rawEntityDefinition = await EdsRawDefinitionsService.requestRawEntityDefinition(entityCode, section);

      if (rawEntityDefinition) {
        if (response) {
          rawEntityDefinition = EdsRawDefinitionsService.injectOverrideRenderDefinition(
            response,
            rawEntityDefinition,
            overrideMode,
          );
        }

        const entityDefinition = EdsRawDefinitionsService.convertRawEntityDefinition(rawEntityDefinition);
        shouldUpdateAccordingToOverride && EdsStorageService.setRawEntity(entityCode, rawEntityDefinition, section);
        shouldUpdateAccordingToOverride && EdsStorageService.set(entityCode, entityDefinition, section);

        return entityDefinition;
      }
    }

    return EdsStorageService.getEntity(entityCode, section).entity;
  }

  public static async normalize<NormalizedResponseType extends NormalizedV2Response | NormalizedV2Response[]>(
    response: EDSV2Response | EDSV2ArrayResponse,
    config: EDSAdapterServiceNormalizeConfiguration = {},
  ): Promise<NormalizedResponseType> {
    if (Array.isArray(response)) {
      if (!response.length) return [] as any as Promise<NormalizedResponseType>;
      const [firstEntity] = response;

      const definition = await EdsAdapterService.getEntityDefinition(
        firstEntity.entityName,
        Boolean(config?.forceUpdate),
        firstEntity,
        config?.section,
        config.dynamicOverrideMode,
        false,
      );

      const subEntityKeys: EdsEntityCodes[] = [
        ...new Set(
          Object.keys(firstEntity)
            .map((propertyKey) =>
              typeof firstEntity[propertyKey] === 'object' &&
              !(firstEntity[propertyKey] as any as EDSV2PropertyValueLink)?.displayValue &&
              (firstEntity[propertyKey] as EDSV2Response)?.entityName
                ? (firstEntity[propertyKey] as EDSV2Response)?.entityName
                : undefined,
            )
            .filter(Boolean),
        ),
      ] as EdsEntityCodes[];

      await Promise.all(
        subEntityKeys.map((subEntityKey) =>
          EdsAdapterService.getEntityDefinition(
            subEntityKey,
            Boolean(config?.forceUpdate),
            firstEntity[subEntityKey] as EDSV2Response,
            config?.section,
            config.dynamicOverrideMode,
            false,
          ),
        ),
      );

      if (definition) {
        return Promise.all(
          response
            .filter((response) => !!response)
            .map((entity) => {
              return EdsAdapterService.normalize(entity, {
                ...config,
                shouldUpdateAccordingToOverride: false,
                forceUpdate: false,
              });
            }),
        ) as any as Promise<NormalizedResponseType>;
      }

      return [] as any as Promise<NormalizedResponseType>;
    }

    const { id, type, entityName, overrideRenderDefinition, customFields, ...responseFieldsWithoutId } = response;
    const responseFields = config.includeId ? { id, ...responseFieldsWithoutId } : responseFieldsWithoutId;

    const entityDefinition = await EdsAdapterService.getEntityDefinition(
      entityName,
      Boolean(config?.forceUpdate),
      response,
      config?.section,
      config.dynamicOverrideMode,
      config?.shouldUpdateAccordingToOverride,
    );

    const fieldsWithCustom = { ...responseFields, ...(customFields || {}) };

    if (!entityDefinition) throw new Error('System: Definition not found');
    const entityFields = entityDefinition?.fields;

    const knownFields: Record<string, NormalizedV2Property | undefined> = {};
    const unknownFields: Record<string, EDSV2Response | EDSV2PropertyValue | undefined> = {};
    const normalizedEntities: Record<string, NormalizedV2Response> = {};

    for (const fieldKey of Object.keys(fieldsWithCustom)) {
      const fieldValue = fieldsWithCustom[fieldKey];

      const isUnknownField = !(fieldKey in entityFields);
      const isEntityField = typeof fieldValue === 'object' && (fieldValue as EDSV2Response)?.entityName;
      const isArrayOfEntities = Array.isArray(fieldValue) && fieldValue.length && fieldValue[0]?.entityName;

      if (isUnknownField && !fieldValue) continue;
      if (isEntityField || isArrayOfEntities) {
        normalizedEntities[edsToEntityFormat(fieldKey)] = await EdsAdapterService.normalize<NormalizedV2Response>(
          fieldsWithCustom[fieldKey] as EDSV2Response,
          config,
        );
      } else {
        if (isUnknownField) unknownFields[fieldKey] = fieldValue;
        else {
          const validationDefinition = entityFields[fieldKey]?.validation;
          const fieldDefinition = { ...entityFields[fieldKey] };

          if (validationDefinition?.allowedValues) {
            validationDefinition.allowedValues = EdsAdapterService.prepareAllowedValues(
              validationDefinition.allowedValues,
              fieldDefinition?.dataType,
            );
          }

          if (![null, undefined].includes(fieldValue as any))
            fieldDefinition.value = EdsAdapterService.prepareFieldValue(
              fieldValue as any,
              fieldDefinition?.dataType === EdsDataTypes.Currency ? EdsDataTypes.Long : fieldDefinition?.dataType,
            ) as any;

          fieldDefinition.displayValue =
            validationDefinition?.allowedValues?.find(
              (option: RawEntityPropertyValidationAllowedValue) => option?.value == fieldValue,
            )?.label || EdsAdapterService.prepareFieldValue(fieldValue as any, fieldDefinition?.dataType);

          knownFields[fieldKey] = MergeService.deepMergeObjects(
            {
              entityId: id,
              entityName: entityName,
              ...fieldDefinition,
            },
            config?.override?.[entityName]?.[fieldKey] || {},
          ) as any;
        }
      }
    }

    const normalizedEntity = {
      id,
      type,
      entityName,
      entities: normalizedEntities,
      virtualFields: unknownFields,
      fields: EdsAdapterService.injectOverrideRenderDefinitionToNormalizedFields(
        knownFields,
        overrideRenderDefinition,
        config.dynamicOverrideMode,
      ),
    };

    return normalizedEntity as any as Promise<NormalizedResponseType>;
  }

  public static generateSlotRecordIncludingEntities<SlotEnum extends string>(
    data: NormalizedV2Response,
    includeArray?: boolean,
    prioritizeCategoryOverSlot?: boolean,
  ): EDSLayoutSlots<SlotEnum> {
    if (!data) return { unknown: [], categories: {} } as EDSLayoutSlots<SlotEnum>;

    const slotList: EDSLayoutSlots<SlotEnum>[] = [];

    const entityOwnSlots: EDSLayoutSlots<SlotEnum> = this.generateSlotRecord<SlotEnum>(
      data,
      prioritizeCategoryOverSlot,
    );
    slotList.push(entityOwnSlots);

    for (const subEntity of Object.values(data?.entities || {})) {
      if (Array.isArray(subEntity) && !includeArray) continue;

      slotList.push(
        ...(Array.isArray(subEntity)
          ? subEntity.map((subEntityItem) => {
              return this.generateSlotRecordIncludingEntities<SlotEnum>(
                subEntityItem,
                includeArray,
                prioritizeCategoryOverSlot,
              );
            })
          : [this.generateSlotRecordIncludingEntities<SlotEnum>(subEntity, includeArray, prioritizeCategoryOverSlot)]),
      );
    }

    return mergeSlots(...slotList);
  }

  public static generateSlotRecord<SlotEnum extends string>(
    data: NormalizedV2Response,
    prioritizeCategoryOverSlot?: boolean,
  ): EDSLayoutSlots<SlotEnum> {
    if (!data) return { unknown: [], categories: {} } as EDSLayoutSlots<SlotEnum>;

    return Object.keys(data.fields).reduce<EDSLayoutSlots<SlotEnum>>(
      (slotsRecord, fieldKey) => {
        const field = data.fields[fieldKey];
        const slot = field?.fieldGroup || '';
        const category = field?.category || '';
        const nextRecord = { ...slotsRecord, categories: { ...(slotsRecord.categories || {}) } };
        const previousSlotFields = nextRecord?.[slot] || [];
        const previousCategoryFields = (nextRecord.categories as EdsCategoriesRecord)?.[category] || [];

        if (slot && !prioritizeCategoryOverSlot) {
          nextRecord[slot] = [...previousSlotFields, field];
        } else if (category) {
          (nextRecord.categories as EdsCategoriesRecord)[category] = field
            ? [...previousCategoryFields, field]
            : previousCategoryFields;
        } else {
          field && nextRecord.unknown?.push(field);
        }

        return nextRecord;
      },
      { unknown: [], categories: {} } as any,
    );
  }

  public static injectOverrideRenderDefinitionToNormalizedFields(
    fieldsRecord: Record<string, NormalizedV2Property | undefined>,
    overrideRenderDefinition?: null | EDSV2OverrideRenderDefinitionResponse,
    overrideMode?: EdsDynamicOverrideMode,
  ): Record<string, NormalizedV2Property | undefined> {
    overrideMode = overrideMode || EdsDynamicOverrideMode.Replace;
    const isReadOnlyOverride = overrideRenderDefinition?.isReadOnly;

    if (isReadOnlyOverride) {
      fieldsRecord = Object.fromEntries(
        Object.entries(fieldsRecord).map(([propertyName, field]) => {
          return [propertyName, { ...field, isEditable: false }];
        }),
      ) as Record<string, NormalizedV2Property | undefined>;
    }

    if (overrideRenderDefinition?.properties?.length) {
      return [...overrideRenderDefinition.properties].reduce((acc, property) => {
        const { propertyName, ...otherSettings } = property;
        const edsAlignedPropertyName = edsToCodeFormat(propertyName);

        if (edsAlignedPropertyName in acc) {
          const partialRenderOverride = Object.keys(otherSettings).reduce(
            (acc, settingKey) => {
              if (otherSettings[settingKey] !== undefined) {
                return { ...acc, ...EdsRawDefinitionsService.adaptPropertySetting(settingKey, otherSettings) };
              }
              return acc;
            },
            {} as Record<string, NormalizedV2Property>,
          );
          const propertyDefinition = fieldsRecord[edsAlignedPropertyName] || {};

          return {
            ...acc,
            [edsAlignedPropertyName]:
              overrideMode === EdsDynamicOverrideMode.Replace
                ? {
                    ...propertyDefinition,
                    ...partialRenderOverride,
                  }
                : MergeService.deepMergeObjects(propertyDefinition, partialRenderOverride),
          } as Record<string, NormalizedV2Property | undefined>;
        }

        return acc;
      }, structuredClone(fieldsRecord));
    }

    return fieldsRecord;
  }

  public static extractLabelFromAllowedValues(field?: NormalizedV2Property, value?: EDSV2PropertyValue) {
    if (!field) return;

    return field.validation?.allowedValues?.find((allowedValue) => allowedValue?.value === value)?.label;
  }

  public static prepareFieldValue(
    value: NormalizedV2Property['value'],
    type?: EdsDataTypes | null,
  ): EDSV2PropertyValue {
    if (type === EdsDataTypes.Currency) {
      if (!value || !TypeService.isNumber(value)) return value;
      return FormatService.formatCurrency(value.toString(), { style: 'currency' });
    }
    if (type === EdsDataTypes.Long) {
      if (!value || !TypeService.isNumber(value)) return value;
      return FormatService.formatCurrency(value.toString(), { style: 'decimal' });
    }
    if (type === EdsDataTypes.Phone) {
      if (!value || typeof value !== 'string') return value;
      return MaskService.formatPhoneForDisplay(value);
    }
    return value;
  }

  public static async renormalize(entity: NormalizedV2Response, section: EdsDefinitionSections) {
    const definition = await EdsAdapterService.getEntityDefinition(
      entity.entityName as EdsEntityCodes,
      false,
      undefined,
      section,
    );
    if (!definition) {
      return entity;
    }
    const fieldsOverride = Object.fromEntries(
      Object.entries(definition.fields)
        .map(([fieldName, field]) => {
          const fieldValue = entity.fields[fieldName]?.value;
          const displayValue = entity.fields[fieldName]?.displayValue
            ? EdsAdapterService.prepareFieldValue(
                entity.fields[fieldName].displayValue,
                definition.fields[fieldName]?.dataType,
              )
            : entity.fields[fieldName]?.displayValue;

          if (fieldValue === undefined) {
            return null;
          }
          return [
            fieldName,
            { ...entity.fields[fieldName], ...field, value: fieldValue, displayValue: displayValue || fieldValue },
          ];
        })
        .filter((fieldSpec) => !!fieldSpec),
    );

    return { ...entity, fields: fieldsOverride };
  }

  public static prepareAllowedValues(
    allowedValues: RawEntityPropertyValidationAllowedValue[],
    type?: EdsDataTypes | null,
  ): RawEntityPropertyValidationAllowedValue[] {
    if (type === EdsDataTypes.YesNo || type === EdsDataTypes.Boolean) {
      return selectYesNoOptions;
    }
    return allowedValues;
  }
}

export default EdsAdapterService;
